import clsx from 'clsx';
import { useField, useFormikContext } from 'formik';
import type { FC } from 'react';
import InputError from '~/components/feedback/InputError';
import { CHECKBOX_TEST_IDS } from '~/components/inputs/Checkbox/constants';
import { interpolateLinks } from '~/components/inputs/Checkbox/utils';
import config from '~/config';
import type { ICheckboxInput } from '~/types/inputs';
import { getInputSizeClasses } from '~/utils/form';
import './style.css';

const Checkbox: FC<
	ICheckboxInput & {
		privacyPolicyRedirect?: string;
		termsRedirect?: string;
	}
> = ({ name, label, required, width, privacyPolicyRedirect = '', termsRedirect = '' }) => {
	const [field, meta] = useField(name);
	const { submitCount } = useFormikContext();
	const isError = (meta.touched || submitCount > 0) && !!meta.error;

	return (
		<div
			className={clsx(
				'checkbox-container relative flex items-start justify-start',
				getInputSizeClasses(width),
			)}
		>
			<div className="relative cursor-pointer mr-4 flex items-center justify-center">
				<input
					{...field}
					type="checkbox"
					className="checkbox-input peer absolute w-full h-full opacity-0 appearance-none cursor-pointer"
					id={name}
					data-testid={CHECKBOX_TEST_IDS.CHECKBOX_INPUT}
				/>
				<div
					className={clsx(
						'checkbox-pseudo-input cursor-pointer w-checkbox h-checkbox rounded-checkbox inline-flex items-center justify-center peer-focus:ring-4',
						isError ? 'is-error' : '',
					)}
				>
					<svg
						xmlns="http://www.w3.org/2000/svg"
						width="18.353"
						height="14.011"
						viewBox="0 0 18.353 14.011"
					>
						<g>
							<path
								d="m2.121 7.547 4.343 4.343 9.768-9.769"
								fill="none"
								stroke="currentColor"
								strokeLinecap={config.areElementsRounded ? 'round' : 'butt'}
								strokeWidth="3"
							/>
						</g>
					</svg>
				</div>
			</div>
			<label
				htmlFor={name}
				className="checkbox-label font-large-body text-sm cursor-pointer text-left pt-1"
			>
				{interpolateLinks(label, {
					privacyPolicyRedirect,
					termsRedirect,
				})}
				{required && !label.endsWith('*') ? <sup>*</sup> : null}
			</label>
			<InputError name={name} isError={isError}>
				{meta.error}
			</InputError>
		</div>
	);
};

export default Checkbox;
